import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUploadFile } from "../../hooks/global/UploadFile";
import { toast } from "react-toastify";

function ImageUploader({
  index,
  canvasWidth = 300,
  canvasHeight = 300,
  picWidth = 200,
  picHeight = 200,
  maxZoom = 3,
  location = "",
  onSave,
}) {
  const { t } = useTranslation();
  const { mutate: uploadFile, isLoading, data } = useUploadFile();

  const canvasRef = useRef(null);
  const [name, setName] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [image, setImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (data) {

      onSave?.({
        type: "onSave",
        data: data.file,
        index: index,
      });
    }
  }, [data, index, onSave]);

  useEffect(() => {
    if (image) {
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      canvas.className = "imageLoaded";

      const renderImage = () => {
        // Clear canvas
        context.clearRect(0, 0, canvas.width, canvas.height);

        if (image) {
          const scaledImageWidth = image.width * zoom;
          const scaledImageHeight = image.height * zoom;

          const offsetX = dragOffset.x - (scaledImageWidth - image.width) / 2;
          const offsetY = dragOffset.y - (scaledImageHeight - image.height) / 2;

          context.drawImage(
            image,
            offsetX,
            offsetY,
            scaledImageWidth,
            scaledImageHeight
          );
        }

        // Draw rectangles on the edges
        const rectWidth = (canvas.width - picWidth) / 2;
        const rectHeight = (canvas.height - picHeight) / 2;

        // Set the fill style for the rectangles
        context.fillStyle = "rgba(0, 0, 0, 0.4)";

        // Draw the rectangles
        context.fillRect(0, 0, canvas.width, rectHeight);
        context.fillRect(
          canvas.width - rectWidth,
          rectWidth,
          rectWidth,
          canvas.height - rectHeight * 2
        );
        context.fillRect(
          0,
          canvas.height - rectHeight,
          canvas.width,
          rectHeight
        );
        context.fillRect(
          0,
          rectHeight,
          rectWidth,
          canvas.height - rectHeight * 2
        );
      };
      renderImage();
      setDisableSubmit(false);
    }
  }, [image, zoom, dragOffset, picWidth, picHeight]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file.type.includes("image")) {
      toast.error(this.props.t("noPic"), { className: "errorToast" });
      return;
    }
    setName(event.target.value);
    setSelectedFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const image = new Image();
        image.onload = () => {
          const scaleFactor = Math.min(
            picWidth / image.width,
            picHeight / image.height
          );

          setDragOffset({
            x: (canvasWidth - image.width) / 2,
            y: (canvasHeight - image.height) / 2,
          });
          setZoom(scaleFactor);
          setImage(image);
        };
        image.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (event) => {
    if (!image) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    setDragStart({ x: offsetX, y: offsetY });
  };

  const handleMouseMove = (event) => {
    if (!selectedFile || !image) return;
    if (event.buttons !== 1) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    const dragOffsetX = offsetX - dragStart.x;
    const dragOffsetY = offsetY - dragStart.y;

    setDragOffset((prevDragOffset) => ({
      x: prevDragOffset.x + dragOffsetX,
      y: prevDragOffset.y + dragOffsetY,
    }));

    setDragStart({ x: offsetX, y: offsetY });
  };

  const handleZoomChange = (event) => {
    const zoomValue = parseFloat(event.target.value);
    setZoom(zoomValue);
  };

  const handleUpload = () => {
    const canvas = canvasRef.current;
    const tempCanvas = document.createElement("canvas");
    const tempContext = tempCanvas.getContext("2d");

    tempCanvas.width = picWidth;
    tempCanvas.height = picHeight;

    const rectWidth = (canvas.width - picWidth) / 2;
    const rectHeight = (canvas.height - picHeight) / 2;

    // Apply transformation to the image on the temporary canvas
    tempContext.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempContext.translate(rectWidth, rectHeight);
    tempContext.drawImage(canvas, -rectWidth * 2, -rectHeight * 2);

    // Convert the cropped canvas to a data URL
    const imageURL = tempCanvas.toDataURL();
    //setPreviewUrl(tempContextDataURL);

    uploadFile({ imageURL, name, location });
  };

  if (isLoading) return "Upload in progress...";

  return (
    <div>
      <div className="imageCanvas text-center">
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          style={{ border: "1px solid #414146" }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
        ></canvas>
      </div>

      <div className="uploadInput text-center">
        <label htmlFor="imageUploader">{t("admin.group.picUp")}</label>
        <input
          type="file"
          accept="image/*"
          id="imageUploader"
          onChange={handleFileChange}
        />
      </div>

      <div className="canvasSettings canvasSettingsZoom">
        <span>{t("admin.group.zoom")}</span>
        <br />
        <input
          style={{ width: "100%" }}
          onChange={handleZoomChange}
          type="range"
          min="0.1"
          max={maxZoom}
          step="0.01"
          value={zoom}
        />
      </div>
      {!disableSubmit && (
        <div>
          <button
            type="button"
            disabled={disableSubmit}
            onClick={() => handleUpload()}
          >
            {t("admin.common.save")}
          </button>
          <br />
        </div>
      )}
    </div>
  );
}
export default ImageUploader;
